import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { AlertTriangle, Users, Home, UserCircle, RefreshCw, TrendingUp } from 'lucide-react';

function RiskBadge({ level }) {
  const variants = {
    HIGH: 'bg-red-500/10 text-red-600 border-red-500/20',
    MEDIUM: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
    LOW: 'bg-green-500/10 text-green-600 border-green-500/20',
  };
  return (
    <Badge variant="outline" className={variants[level] || 'bg-muted'}>
      {level}
    </Badge>
  );
}

function RiskCard({ name, score, riskLevel, factors, entityType, onClick }) {
  return (
    <Card 
      className="cursor-pointer hover:border-primary/50 transition-colors"
      onClick={onClick}
      data-testid={`risk-card-${entityType}-${name}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="font-medium truncate">{name}</p>
            <div className="flex items-center gap-2 mt-1">
              <RiskBadge level={riskLevel} />
              <span className="text-sm text-muted-foreground">Score: {score?.toFixed(1)}</span>
            </div>
            {factors?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {factors.slice(0, 3).map((f, i) => (
                  <span key={i} className="text-xs bg-muted px-2 py-0.5 rounded">
                    {f.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className={`h-12 w-12 rounded-full flex items-center justify-center ${
            riskLevel === 'HIGH' ? 'bg-red-500/10' :
            riskLevel === 'MEDIUM' ? 'bg-amber-500/10' : 'bg-green-500/10'
          }`}>
            <span className={`text-lg font-bold ${
              riskLevel === 'HIGH' ? 'text-red-600' :
              riskLevel === 'MEDIUM' ? 'text-amber-600' : 'text-green-600'
            }`}>
              {Math.round(score || 0)}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function KpiCard({ title, value, subtitle, icon: Icon, variant = 'default' }) {
  const variants = {
    default: 'bg-card',
    danger: 'bg-red-500/5 border-red-500/20',
    warning: 'bg-amber-500/5 border-amber-500/20',
    success: 'bg-green-500/5 border-green-500/20',
  };
  return (
    <Card className={variants[variant]} data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          {Icon && (
            <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
              variant === 'danger' ? 'bg-red-500/10' :
              variant === 'warning' ? 'bg-amber-500/10' :
              variant === 'success' ? 'bg-green-500/10' : 'bg-primary/10'
            }`}>
              <Icon className={`h-5 w-5 ${
                variant === 'danger' ? 'text-red-500' :
                variant === 'warning' ? 'text-amber-500' :
                variant === 'success' ? 'text-green-500' : 'text-primary'
              }`} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function RiskGrid({ scores, entityType }) {
  if (!scores || scores.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No {entityType.toLowerCase()} risk scores found
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {scores.map((s) => (
        <RiskCard
          key={s.id}
          name={s.entityName || 'Unknown'}
          score={s.score}
          riskLevel={s.riskLevel}
          factors={s.factors}
          entityType={entityType}
        />
      ))}
    </div>
  );
}

export default function RiskDashboardPage() {
  const [loading, setLoading] = useState(true);
  const [scores, setScores] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedType, setSelectedType] = useState('PARTICIPANT');
  const [calculating, setCalculating] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [scoresRes, summaryRes] = await Promise.all([
        api.get('/risk-scores', { params: { pageSize: 100 } }),
        api.get('/risk-scores/summary')
      ]);
      setScores(scoresRes.data.items || []);
      setSummary(summaryRes.data.data || {});
    } catch (err) {
      toast.error('Failed to load risk scores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCalculate = async (entityType) => {
    setCalculating(true);
    try {
      await api.post('/risk-scores/calculate', { entityType });
      toast.success(`${entityType} risk scores calculated`);
      fetchData();
    } catch (err) {
      toast.error('Failed to calculate risk scores');
    } finally {
      setCalculating(false);
    }
  };

  const filteredScores = scores.filter(s => s.entityType === selectedType);
  const highRiskCount = summary?.highCount || 0;
  const mediumCount = summary?.mediumCount || 0;
  const lowCount = summary?.lowCount || 0;

  const participantScores = scores.filter(s => s.entityType === 'PARTICIPANT');
  const staffScores = scores.filter(s => s.entityType === 'STAFF');
  const houseScores = scores.filter(s => s.entityType === 'HOUSE');

  return (
    <div className="space-y-6" data-testid="risk-dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Risk Dashboard</h1>
          <p className="text-muted-foreground">Entity risk scores and analysis</p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedType} onValueChange={setSelectedType}>
            <SelectTrigger className="w-40" data-testid="entity-type-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="PARTICIPANT">Participants</SelectItem>
              <SelectItem value="STAFF">Staff</SelectItem>
              <SelectItem value="HOUSE">Houses</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            onClick={() => handleCalculate(selectedType)} 
            disabled={calculating}
            data-testid="calculate-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${calculating ? 'animate-spin' : ''}`} />
            Calculate
          </Button>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KpiCard
          title="Total Scores"
          value={summary?.total || 0}
          icon={TrendingUp}
        />
        <KpiCard
          title="High Risk"
          value={highRiskCount}
          subtitle="Requires attention"
          icon={AlertTriangle}
          variant="danger"
        />
        <KpiCard
          title="Medium Risk"
          value={mediumCount}
          subtitle="Monitor closely"
          icon={AlertTriangle}
          variant="warning"
        />
        <KpiCard
          title="Low Risk"
          value={lowCount}
          subtitle="Acceptable level"
          icon={AlertTriangle}
          variant="success"
        />
      </div>

      {/* Tabs for different entity types */}
      <Tabs value={selectedType} onValueChange={setSelectedType} className="space-y-4">
        <TabsList className="grid w-full grid-cols-3 max-w-md">
          <TabsTrigger value="PARTICIPANT" data-testid="tab-participants">
            <Users className="h-4 w-4 mr-2" />
            Participants ({participantScores.length})
          </TabsTrigger>
          <TabsTrigger value="STAFF" data-testid="tab-staff">
            <UserCircle className="h-4 w-4 mr-2" />
            Staff ({staffScores.length})
          </TabsTrigger>
          <TabsTrigger value="HOUSE" data-testid="tab-houses">
            <Home className="h-4 w-4 mr-2" />
            Houses ({houseScores.length})
          </TabsTrigger>
        </TabsList>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <TabsContent value="PARTICIPANT">
              <RiskGrid scores={participantScores} entityType="PARTICIPANT" />
            </TabsContent>
            <TabsContent value="STAFF">
              <RiskGrid scores={staffScores} entityType="STAFF" />
            </TabsContent>
            <TabsContent value="HOUSE">
              <RiskGrid scores={houseScores} entityType="HOUSE" />
            </TabsContent>
          </>
        )}
      </Tabs>
    </div>
  );
}
